import React, { FC, useState } from 'react'
import IconTextButton, { ButtonType } from 'components/buttons/TextIconButton'
import { t } from '@lingui/macro'

import { PlayIcon } from 'assets/icons/PlayIcon'
import { ShareButton } from './Buttons/ShareButton'
import { TicketsIcon } from 'assets/icons/TicketsIcon'

import { LikeStreamButton } from './Buttons/LikeStreamButton'
import ReplayIcon from 'assets/icons/ReplayIcon'
import useAdminAccess from 'hooks/AdminAccess'
import { userInterface } from 'redux-store/userInterface/userInterface.actions'
import { useDispatch, useSelector } from 'react-redux'
import { useTryOpenViewStream } from 'features/viewerPage/hooks/useTryOpenViewStream'
import useCurrentUser from 'hooks/CurrentUser'
import authenticationActions from 'redux-store/authentication/authentication.actions'
import { AccessRestrictionType } from 'interfaces'
import { streamViewTypeSelector } from 'redux-store/stream/stream.selectors'
import { ViewType } from 'redux-store/stream/stream.interface'
import { isViewingAsAdminSelector } from 'redux-store/roles/roles.selector'
import styles from './CTASection.module.scss'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { ConditionalRender } from 'components/ConditionalRender/ConditionalRender'

interface Props {
  userHasAccess: boolean
  streamIsUpcoming: boolean
  streamHasDVR: boolean
  accessRestriction: AccessRestrictionType
}
export const CTASection: FC<Props> = ({
  userHasAccess,
  streamIsUpcoming,
  streamHasDVR,
  accessRestriction
}) => {
  const dispatch = useDispatch()
  const tryOpenViewStream = useTryOpenViewStream()
  const adminAccess = useAdminAccess()
  const currentUser = useCurrentUser()
  const hasChannelAccess = adminAccess.hasChannelAccess
  const isViewingAsAdmin = useSelector(isViewingAsAdminSelector)
  const viewType = useSelector(streamViewTypeSelector)
  const isNoPastStream = viewType === ViewType.NO_PAST_LIVESTREAM

  const [showTooltip, setShowTooltip] = useState(false)
  const getTextForPrimaryButton = () => {
    if (userHasAccess) {
      if (streamHasDVR) {
        return t`Watch live`
      }
      return t`Watch stream`
    } else {
      if (accessRestriction === AccessRestrictionType.LoginRequired) {
        return t`Sign in to watch`
      }
      if (accessRestriction === AccessRestrictionType.None) {
        return t`Watch stream`
      }
      return t`Buy stream`
    }
  }

  const PrimaryButtonMustBeHidden = () => {
    if (
      userHasAccess ||
      accessRestriction === AccessRestrictionType.LoginRequired ||
      accessRestriction === AccessRestrictionType.None
    ) {
      return false
    }
    return true
  }

  const primaryButton = getTextForPrimaryButton()
  const primaryIconToUse =
    userHasAccess || accessRestriction === AccessRestrictionType.None ? (
      <PlayIcon />
    ) : (
      <TicketsIcon />
    )
  const handlePrimaryButtonClick = () => {
    if (userHasAccess) {
      if (streamHasDVR) {
        dispatch(userInterface.viewDvrFromLive())
      }
      tryOpenViewStream()
    } else {
      if (!currentUser) {
        dispatch(authenticationActions.openLoginLayer())
        return false
      }

      if (accessRestriction === AccessRestrictionType.LoginRequired) {
        dispatch(authenticationActions.openLoginLayer())
        tryOpenViewStream()
      } else if (accessRestriction === AccessRestrictionType.None) {
        tryOpenViewStream()
      } else {
        dispatch(userInterface.openPaymentDialog())
      }
    }
  }
  const handlePlayFromBegining = () => {
    if (streamHasDVR) {
      dispatch(userInterface.viewDvrFromBeginning())
    }
    tryOpenViewStream()
  }

  const showPrimaryButton = userHasAccess
    ? !streamIsUpcoming || (hasChannelAccess && isViewingAsAdmin)
    : true

  const showPlayFromBeginingButton =
    userHasAccess &&
    (!streamIsUpcoming || (hasChannelAccess && isViewingAsAdmin)) &&
    streamHasDVR &&
    viewType !== ViewType.REPLAY
  const renderedPrimaryButton = () => {
    return (
      <IconTextButton
        id={'primary-button'}
        type={ButtonType.CTA}
        buttonSize={'Large'}
        label={primaryButton}
        onClick={handlePrimaryButtonClick}
        isRounded
        data-testid="primary-button"
        externalStyle={styles.PrimaryButton}
      >
        {primaryIconToUse}
      </IconTextButton>
    )
  }

  const renderedPrimaryButtonInMobileApp = () => {
    return (
      <div className={styles.MobileButtonContainer}>
        <div className={styles.Tooltip}>
          {showTooltip && (
            <Tooltip
              title={t`Helpfull tip!`}
              text={'You cannot buy content in this app'}
              pointerPosition="Left"
              confirmButtonText="Got it"
              onConfirmButtonClick={() => setShowTooltip(false)}
            />
          )}
        </div>
        <IconTextButton
          id={'primary-button'}
          type={ButtonType.Primary}
          buttonSize={'Large'}
          label="Stream info"
          onClick={() => setShowTooltip(true)}
          isRounded
          data-testid="primary-button"
          externalStyle={styles.PrimaryButton}
          disabled={isNoPastStream && !isViewingAsAdmin}
        >
          {primaryIconToUse}
        </IconTextButton>
      </div>
    )
  }

  return (
    <div className={styles.Container}>
      {showPrimaryButton && (
        <>
          {/* Web */}
          <ConditionalRender mobileApp={false}>
            {renderedPrimaryButton()}
          </ConditionalRender>
          {/* MobileApp */}
          <ConditionalRender web={false}>
            {PrimaryButtonMustBeHidden()
              ? renderedPrimaryButtonInMobileApp()
              : renderedPrimaryButton()}
          </ConditionalRender>
        </>
      )}

      {showPlayFromBeginingButton && (
        <IconTextButton
          id={'play-from-begining-button'}
          type={ButtonType.Ghost}
          buttonSize={'Medium'}
          label={t`Watch from begining`}
          onClick={handlePlayFromBegining}
          isRounded
          data-testid="watch-from-begining-button"
          externalStyle={styles.WatchFromBeginingButton}
        >
          <ReplayIcon />
        </IconTextButton>
      )}
      <div className={styles.InteractionButtonContainer}>
        {userHasAccess && currentUser && <LikeStreamButton />}
        <ShareButton />
      </div>
    </div>
  )
}
